/**
 * 🎨 Theme Application System
 *
 * Управление темами: localStorage, Telegram theme detection, CSS vars.
 */

import type { ThemeTokens, ThemeName } from './theme-manager';
import { themes } from './theme-manager';
import { THEME_KEY } from '../../constants/storage';
import { applyDesignConfig } from '../../theme/applyDesignConfig';

// Re-export ThemeName for convenience
export type { ThemeName };

const STORAGE_KEY = THEME_KEY;

/**
 * Преобразует ThemeTokens в CSS custom properties
 */
export function toCssVars(tokens: ThemeTokens): Record<string, string> {
  return {
    // Surfaces
    '--c-bg': tokens.bg,
    '--c-surface': tokens.surface,
    '--c-card': tokens.card,
    '--c-elevated': tokens.elevated,

    // Text
    '--c-text': tokens.text,
    '--c-text-muted': tokens.textMuted,
    ...(tokens.textTertiary && { '--c-text-tertiary': tokens.textTertiary }),
    ...(tokens.textPlaceholder && { '--c-text-placeholder': tokens.textPlaceholder }),

    // Primary
    '--c-primary': tokens.primary,
    '--c-on-primary': tokens.onPrimary,
    ...(tokens.link && { '--c-link': tokens.link }),
    ...(tokens.onLightSurface && { '--c-on-light-surface': tokens.onLightSurface }),
    ...(tokens.primaryLight && { '--c-primary-light': tokens.primaryLight }),
    ...(tokens.onPrimaryContainer && { '--c-on-primary-container': tokens.onPrimaryContainer }),

    // Secondary (optional)
    ...(tokens.secondary && { '--c-secondary': tokens.secondary }),
    ...(tokens.onSecondary && { '--c-on-secondary': tokens.onSecondary }),

    // Borders
    '--c-border': tokens.border,
    '--c-border-strong': tokens.borderStrong,

    // Scrollbar (график аналитики и др.)
    '--c-scrollbar-track': tokens.scrollbarTrack ?? tokens.surface,
    '--c-scrollbar-thumb': tokens.scrollbarThumb ?? tokens.border,
    '--c-scrollbar-thumb-hover': tokens.scrollbarThumbHover ?? tokens.textMuted,

    // Semantic colors
    '--c-success': tokens.success,
    '--c-on-success': tokens.onSuccess,
    '--c-warning': tokens.warning,
    '--c-on-warning': tokens.onWarning,
    '--c-error': tokens.error,
    '--c-on-error': tokens.onError,
    '--c-info': tokens.info,
    '--c-on-info': tokens.onInfo,

    // Interactive states
    ...(tokens.focus && { '--c-focus': tokens.focus }),
    ...(tokens.disabled && { '--c-disabled': tokens.disabled }),

    // Scrim
    '--c-scrim': tokens.scrim,

    // Appointment statuses
    '--c-appt-scheduled-bg': tokens.apptScheduledBg,
    '--c-appt-completed-bg': tokens.apptCompletedBg,
    '--c-appt-no-show-bg': tokens.apptNoShowBg,
    '--c-appt-cancelled-bg': tokens.apptCancelledBg,
    '--c-appt-moved-bg': tokens.apptMovedBg,
    '--c-appt-break-bg': tokens.apptBreakBg,
  };
}

/**
 * Применяет CSS переменные к DOM элементу
 */
export function applyCssVars(vars: Record<string, string>, target: HTMLElement = document.documentElement): void {
  Object.entries(vars).forEach(([property, value]) => {
    target.style.setProperty(property, value);
  });
}

/**
 * Определяет начальную тему
 *
 * Приоритет:
 * 1. localStorage (если сохранена валидная тема)
 * 2. Telegram WebApp theme (dark/light)
 * 3. 'light' по умолчанию
 * 
 * Примечание: VK схема НЕ используется для автоматического выбора темы.
 * Пользователь выбирает тему в настройках, и она сохраняется в localStorage.
 */
export function getInitialTheme(): ThemeName {
  // 1. Проверить localStorage
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in themes) {
    return stored as ThemeName;
  }

  // 2. Проверить Telegram WebApp theme
  try {
    // @ts-ignore - Telegram WebApp API
    if (window.Telegram?.WebApp?.colorScheme) {
      // @ts-ignore
      const tgTheme = window.Telegram.WebApp.colorScheme;
      if (tgTheme === 'dark') return 'dark';
      if (tgTheme === 'light') return 'light';
    }
  } catch (e) {
    // Telegram API недоступен, продолжаем
  }

  // 3. По умолчанию auto (системный)
  return 'auto';
}

/**
 * Возвращает текущую тему (из localStorage или начальную)
 */
export function getTheme(): ThemeName {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored && stored in themes) {
    return stored as ThemeName;
  }
  return getInitialTheme();
}

/**
 * Устанавливает тему: сохраняет в localStorage и применяет CSS vars
 */
export function setTheme(name: ThemeName): void {
  // Сохранить в localStorage
  localStorage.setItem(STORAGE_KEY, name);

  // Определяем итоговую тему (auto → системная)
  const resolvedName: ThemeName = name === 'auto'
    ? (typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light')
    : name;

  // Применить CSS переменные
  const themeTokens = themes[resolvedName];
  const cssVars = toCssVars(themeTokens);
  applyCssVars(cssVars);

  if (typeof document !== 'undefined' && document.documentElement) {
    document.documentElement.setAttribute('data-theme', resolvedName);
  }

  // Отправить событие для компонентов (для синхронизации UI)
  window.dispatchEvent(new CustomEvent('theme-changed', { detail: resolvedName }));

  applyDesignConfig();
}

/**
 * Инициализирует тему при старте приложения
 */
export function initializeTheme(): ThemeName {
  const initialTheme = getInitialTheme();
  setTheme(initialTheme);
  return initialTheme;
}
